import React, { useState } from 'react';
import { X, Building, Hash } from 'lucide-react';
import { baserowAPI, Company, TABLES } from '../services/baserowAPI';
import { invalidateCompaniesCache } from '../hooks/useCompanies';

interface AddCompanyModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCompanyCreated?: (company: Company) => void;
}

export const AddCompanyModal: React.FC<AddCompanyModalProps> = ({
  isOpen,
  onClose,
  onCompanyCreated,
}) => {
  const [formData, setFormData] = useState({
    name: '',
    siret: '',
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const validateSiret = (siret: string): boolean => {
    if (!siret) return true; // SIRET optionnel
    
    // Supprimer les espaces et caractères non numériques
    const cleanSiret = siret.replace(/\s/g, '');
    
    // Vérifier que c'est bien 14 chiffres
    if (!/^\d{14}$/.test(cleanSiret)) {
      return false;
    }

    return true;
  };

  const formatSiret = (siret: string): string => {
    // Supprimer tout ce qui n'est pas un chiffre
    const cleaned = siret.replace(/\D/g, '');
    
    // Limiter à 14 chiffres
    const limited = cleaned.substring(0, 14);
    
    // Formatter avec des espaces (XXX XXX XXX XXXXX)
    if (limited.length <= 3) return limited;
    if (limited.length <= 6) return `${limited.substring(0, 3)} ${limited.substring(3)}`;
    if (limited.length <= 9) return `${limited.substring(0, 3)} ${limited.substring(3, 6)} ${limited.substring(6)}`;
    return `${limited.substring(0, 3)} ${limited.substring(3, 6)} ${limited.substring(6, 9)} ${limited.substring(9)}`;
  };

  const handleSiretChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatSiret(e.target.value);
    setFormData(prev => ({
      ...prev,
      siret: formatted
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      setError('Le nom de l\'entreprise est obligatoire');
      return;
    }

    if (formData.siret && !validateSiret(formData.siret)) {
      setError('Le SIRET doit contenir exactement 14 chiffres');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Créer l'entreprise avec les champs Baserow appropriés
      const newCompany = await baserowAPI.createRow<Company>(TABLES.COMPANIES, {
        field_7248: formData.name.trim(), // Name
        field_7259: formData.siret.trim() || null, // SIRET
      });

      console.log('Entreprise créée avec succès:', newCompany);
      
      // Réinitialiser le cache des entreprises
      invalidateCompaniesCache();
      
      // Réinitialiser le formulaire
      setFormData({
        name: '',
        siret: '',
      });

      // Notifier le parent
      if (onCompanyCreated) {
        onCompanyCreated(newCompany);
      }

      // Fermer la modal
      onClose();
    } catch (err) {
      console.error('Erreur lors de la création de l\'entreprise:', err);
      setError('Erreur lors de la création de l\'entreprise. Veuillez réessayer.');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg max-w-md w-full">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <div className="flex items-center">
            <Building className="w-6 h-6 text-green-600 mr-2" />
            <h2 className="text-xl font-semibold">Créer une nouvelle entreprise</h2>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6">
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md text-red-700 text-sm">
              {error}
            </div>
          )}

          <div className="space-y-4">
            {/* Nom de l'entreprise (obligatoire) */}
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                Nom de l'entreprise *
              </label>
              <div className="relative">
                <Building className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="text"
                  id="name"
                  name="name"
                  required
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Ex: ACME Corporation, Entreprise Dupont SARL..."
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500 focus:border-transparent"
                />
              </div>
            </div>

            {/* SIRET (optionnel) */}
            <div>
              <label htmlFor="siret" className="block text-sm font-medium text-gray-700 mb-2">
                SIRET (optionnel)
              </label>
              <div className="relative">
                <Hash className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="text"
                  id="siret"
                  name="siret"
                  value={formData.siret}
                  onChange={handleSiretChange}
                  placeholder="123 456 789 01234"
                  maxLength={17} // 14 chiffres + 3 espaces
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500 focus:border-transparent"
                />
              </div>
              <p className="mt-1 text-xs text-gray-500">
                Format: 14 chiffres (formatage automatique)
              </p>
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end space-x-4 mt-6 pt-4 border-t">
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              className="px-4 py-2 text-gray-600 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors disabled:opacity-50"
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={loading || !formData.name.trim()}
              className="px-6 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Création...' : 'Créer l\'entreprise'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
